import React, { useEffect, useMemo, useState } from "react";
import { Table, Typography, Spin, Button, Tooltip, Modal, Space, Select, Descriptions } from "antd";
import ExpandCollapseAllButton from "../../common/ExpandCollapseAllButton";
import { InfoCircleOutlined, PlusOutlined, FolderAddOutlined, EditOutlined, DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import NumberInput from "../../common/NumberInput";
import BcCalculationDetails, { computeBcCalculation, SummaryKeyRawFields } from "../../common/BcCalculationDetails";
import { useTender } from "../../../contexts/TenderContext";
import { useDevMode } from "@/contexts/DevModeContext";
import { useCanConfigureBoq } from "@/hooks/useCanConfigureBoq";
import { generateBOQSummaryWithCategories } from "../../../utils/boqSummaryGenerator";
import { COLORS, formatUnit, MAX_SECTION_NESTING_DEPTH, MAX_SECTION_NESTING_DEPTH_MESSAGE, buildSectionDepthMap } from "../../../utils/constants";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { useGlobalSettings } from "../../../hooks/useGlobalSettings";
import {
  createScaffoldRateLookupMap,
  applyScaffoldRatesToSummary,
  SCAFFOLD_M3_CONVERSION_FIELDS,
} from "../../../utils/scaffoldItemLibraryIntegration";
import { getScaffoldConfigureHeadersViolationsByField, type FieldConfigureHeadersViolation } from "../../../utils/groundworksConfigureHeadersValidation";
import { SCAFFOLD_CATEGORIES } from "../../../utils/billSummaryCategories";
import { formatDimensionLabel } from "../../../utils/dimensionLabel";
import { useBoqLatestVersion } from "../../../hooks/useBillOfQuantities";
import { BoqSummaryLineItem } from "../../../utils/groundworksBoqSummaryComputer";
import { computeDynamicBcCalculation, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows } from '../../../hooks/useBillSummaryExtraItems';
import { generatePlotMatrixCategoryColumns } from '../../../utils/plotMatrixCategoryGenerator';
import type { DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === "section") {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Header/section rowTypes across this table — 'sub-header' is the only one
// the FIXED data ever produces; 'main-header'/'column-type-header' only ever
// come from ad-hoc extra Section rows (root-level / nested respectively —
// see useBillSummaryExtraItems.tsx's buildExtraRow depth convention), but
// every column below still has to treat all three the same way so a
// newly-added extra section renders consistently instead of falling through
// to leaf-row rendering. Distinct from 'breakdown-row', Scaffold's own
// existing dev-mode rate-breakdown child rows, which stay their own case.
const isHeaderRowType = (rowType: string) =>
  rowType === "sub-header" || rowType === "main-header" || rowType === "column-type-header";

// Fields to consolidate per section: Level & Compact, Geotextile Layer, crush 6F2, Type1.
// The consolidated item's Rate = sum of the Level & Compact / Crush 6F2 / Type1
// rates, plus the Geotextile Layer rate ONLY when that section's "Geotextile
// Layer" checkbox (Scaffold → Configure Headers) is on — see geotextileEnabledKey,
// which reads scaffoldDimensionValues[key] === "true".
const CONSOLIDATE_FIELDS: Record<string, { quantityField: string; rateFields: string[]; crushField: string; type1Field: string; geotextileField: string; geotextileEnabledKey: string }> = {
  "Scaffold Platforms": {
    quantityField: "scaffoldPlatform",
    rateFields: ["scaffoldLevelCompact", "scaffoldGeotextileLayer", "scaffoldCrush6F2", "scaffoldType1"],
    crushField: "scaffoldCrush6F2",
    type1Field: "scaffoldType1",
    geotextileField: "scaffoldGeotextileLayer",
    geotextileEnabledKey: "scaffoldGeotextileEnabled",
  },
  "Loading Bays": {
    quantityField: "loadingBay4x2m",
    rateFields: ["loadingLevelCompact", "loadingGeotextileLayer", "loadingCrush6F2", "loadingType1"],
    crushField: "loadingCrush6F2",
    type1Field: "loadingType1",
    geotextileField: "loadingGeotextileLayer",
    geotextileEnabledKey: "loadingGeotextileEnabled",
  },
  "Stair Access": {
    quantityField: "stairAccess3x3m",
    rateFields: ["stairLevelCompact", "stairGeotextileLayer", "stairCrush6F2", "stairType1"],
    crushField: "stairCrush6F2",
    type1Field: "stairType1",
    geotextileField: "stairGeotextileLayer",
    geotextileEnabledKey: "stairGeotextileEnabled",
  },
};

function buildConsolidatedDescription(
  crushField: string,
  type1Field: string,
  dimensionValues: Record<string, string>,
  geotextileEnabled = false,
): string {
  const crushValue = dimensionValues[crushField];
  const type1Value = dimensionValues[type1Field];
  const crushDim = crushValue ? formatDimensionLabel(crushValue) : "xxx";
  const type1Dim = type1Value ? formatDimensionLabel(type1Value) : "xx";
  return `${crushDim} crushed concrete, blinded with ${type1Dim} thick Type 1${geotextileEnabled ? " including geotextile layer" : ""}`;
}


function getRateFieldLabel(fieldName: string, dimensionValues: Record<string, string>): string {
  if (fieldName.includes("LevelCompact")) return "Level & Compact";
  if (fieldName.includes("GeotextileLayer")) return "Geotextile Layer";
  if (fieldName.includes("Crush6F2")) {
    const dim = dimensionValues[fieldName];
    return dim ? `${formatDimensionLabel(dim)} Crush 6F2` : "Crush 6F2";
  }
  if (fieldName.includes("Type1")) {
    const dim = dimensionValues[fieldName];
    return dim ? `${formatDimensionLabel(dim)} Type 1` : "Type 1";
  }
  return fieldName;
}

const { Text, Title } = Typography;

interface ScaffoldSummaryItem {
  key: string;
  itemNumber: string;
  fieldName: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  category: string;
  resourceCode?: string;
  breakdownRows?: BreakdownRow[];
  geotextileEnabled?: boolean;
  geotextileField?: string;
  // The fieldName the persisted `groundworks.summary.scaffold` API line item is keyed by.
  // For consolidated platform rows this is the underlying quantity field (e.g.
  // "scaffoldPlatform"), not the UI's own synthetic `consolidated-<category>` fieldName.
  apiFieldName?: string;
}

interface BreakdownRow {
  key: string;
  fieldName: string;
  description: string;
  quantity: number;
  unit: string;
  rate: number;
  amount: number;
  resourceCode?: string;
  // dbRate: the original m³ rate before the m³→m² thickness multiplier was
  // applied. For non-converted fields (Level & Compact, Geotextile Layer)
  // this equals `rate`; for converted fields (Crush 6F2, Type 1) it's
  // `rate / thicknessInMetres`. Rendered only in dev mode.
  dbRate?: string;
  // m³→m² thickness dimension (e.g. "150mm") for Crush 6F2 / Type1 fields —
  // mirrors the `multiplier` set on the persisted API child in
  // `computeScaffoldSection` (groundworksBoqSummaryComputer.ts). Absent for
  // Level & Compact / Geotextile Layer, which carry no rate conversion.
  multiplier?: string;
  // Tagged so the existing column renderers (which already key off this
  // rowType for muted styling) treat the rows correctly when attached as
  // expandable children of the consolidated line item in dev mode.
  rowType?: 'breakdown-row';
}

/**
 * Recover the original m³ rate for a Scaffold field. Mirrors the inverse of
 * `getM3ToM2Multiplier` in scaffoldItemLibraryIntegration: for fields in
 * `SCAFFOLD_M3_CONVERSION_FIELDS`, divide the effective m² rate by the
 * thickness in metres (`value >= 1 → mm, /1000`; `value < 1 → metres`). For
 * non-converted fields, the m² rate IS the db rate.
 */
function computeScaffoldDbRate(
  fieldName: string,
  rate: number,
  dimensionValues: Record<string, string>,
): number {
  if (!SCAFFOLD_M3_CONVERSION_FIELDS.has(fieldName)) return rate;
  const raw = dimensionValues[fieldName];
  if (!raw) return rate;
  const match = String(raw).trim().match(/^(\d+(?:\.\d+)?)/);
  const value = match ? parseFloat(match[1]) : null;
  if (!value || value <= 0) return rate;
  const mult = value >= 1 ? value / 1000 : value;
  return rate / mult;
}

/**
 * Same mm-or-metres rule as `computeScaffoldDbRate`'s inline conversion, but
 * standalone so it can convert a raw dim string on its own — used to build
 * the consolidated row's `multiplier` as the PRODUCT of Crush 6F2's and
 * Type1's own converted thickness (in metres).
 */
function parseThicknessMetres(raw: unknown): number | null {
  if (raw === undefined || raw === null || raw === '') return null;
  const match = String(raw).trim().match(/^(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const value = parseFloat(match[1]);
  if (!value || value <= 0) return null;
  return value >= 1 ? value / 1000 : value;
}

const ScaffoldBillSummary: React.FC = () => {
  const showDevMode = useDevMode();
  // Ad-hoc "Add Item"/"Add Section" extra rows — gated by the "Configure BOQ" switch (next to the Re-calculate
  // button on /groundworks/review), independent of Dev Mode (useCanConfigureBoq(), hooks/useCanConfigureBoq.ts) — same
  // convention as the other groundworks Bill Summaries.
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const { state, updateField } = useTender();
  const { settings } = useGlobalSettings();
  const { data: itemLibraryData, isLoading: isLoadingItemLibrary } = useItemLibrary(state.region || "");
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const [breakdownModalKey, setBreakdownModalKey] = useState<string | null>(null);
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  const [configViolationModalRecord, setConfigViolationModalRecord] = useState<FieldConfigureHeadersViolation | null>(null);
  const configViolationsByField = useMemo(
    () => getScaffoldConfigureHeadersViolationsByField(state.groundworks),
    [state.groundworks],
  );
  // SummaryKey modal for ad-hoc extra items — these have no persisted boq-versions
  // entry (`apiSummaryItem`) since they aren't serialised into the exported BOQ
  // payload yet, so this shows the live-derived value instead, same "unsaved"
  // convention DynamicBillSummaryPanel's own SummaryKey column uses.
  // `persisted` (when set) is the actual raw `BoqSummaryLineItem` fetched from the
  // BOQ API for this extra item's own `fieldName` — shown in full via
  // `SummaryKeyRawFields` so the modal reflects every detail the API actually
  // has, not just the hand-picked subset of `live`'s fields. `live` (the
  // client-side node, always set) still drives the BCCalculation breakdown,
  // since that derivation needs the node's own `resourceCodes` array — a
  // persisted record only ever carries the combined `resourceCode` string.
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<{
    live: DynamicBillSummaryLineItem;
    persisted: BoqSummaryLineItem | null;
  } | null>(null);

  // Ad-hoc user-added Section/Item rows grafted onto this otherwise-immutable
  // computed table — see useBillSummaryExtraItems.tsx. Gated by
  // dynamicSummaryEnabled (the "Configure BOQ" switch), same as every other
  // groundworks Bill Summary's own extra items. `scaffoldExtraItems` is
  // keyed by "attachment point": `"__root__"` for new top-level rows, or an
  // existing computed category row's own `key` for rows grafted onto it.
  const scaffoldExtraItems = state.groundworks?.scaffoldExtraItems;
  // Live Plot Matrix columns, for the Add/Edit Item modal's "Include in Plot
  // Matrix" Valuation Stage lookup — same call GroundworksCutFillBillSummary.tsx
  // makes for its own extra items.
  const extraItemsPlotMatrixColumns = useMemo(
    () => generatePlotMatrixCategoryColumns(state.groundworks as any),
    [state.groundworks],
  );
  // Newly-added extra row (Section or Item) — auto-expands the row it was
  // grafted onto and briefly highlights/autofocuses it, same convention
  // GroundworksCutFillBillSummary.tsx follows for its own Add Section/Add Item.
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);

  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: scaffoldExtraItems,
    onChange: (updated) =>
      updateField("groundworks", { ...state.groundworks, scaffoldExtraItems: updated }),
    itemLibraryData,
    plotMatrixColumns: extraItemsPlotMatrixColumns,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) {
        setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      }
      setNewlyAddedRowId(nodeId);
    },
  });

  // Dev-mode only: pulls the persisted `groundworks.summary.scaffold` line items from the
  // same boq-versions API (`getBoqVersion`) that drives the rest of the review page, so the
  // SummaryKey column reflects what was actually saved rather than the live recomputation above.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );

  const apiScaffoldSummaryByFieldName = useMemo(() => {
    const map = new Map<string, BoqSummaryLineItem>();
    const items = (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ groundworks?: { summary?: { scaffold?: BoqSummaryLineItem[] } } }> } })
      ?.boq?.billOfQuantities?.[0]?.groundworks?.summary?.scaffold;
    (items || []).forEach((item) => {
      if (item.fieldName) map.set(item.fieldName, item);
    });
    return map;
  }, [latestBoqVersionData]);

  // Breakdown rows (Level & Compact / Geotextile Layer / Crush 6F2 / Type1) are persisted
  // as each consolidated item's own `children` array rather than top-level fieldName
  // entries — flatten those into their own lookup so the SummaryKey column can match them too.
  const apiScaffoldChildByFieldName = useMemo(() => {
    const map = new Map<string, BoqSummaryLineItem>();
    apiScaffoldSummaryByFieldName.forEach((parent) => {
      (parent.children || []).forEach((child) => {
        if (child.fieldName) map.set(child.fieldName, child);
      });
    });
    return map;
  }, [apiScaffoldSummaryByFieldName]);

  // Build a code→{rate} lookup directly from item library, for the SummaryKey modal's
  // "BCCalculation" line (same convention as GroundworksCutFillBillSummary).
  const codeRateLookup = useMemo(() => {
    const map = new Map<string, { rate: number }>();
    if (!itemLibraryData) return map;
    itemLibraryData.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) {
        map.set(code, { rate: item.nettRate || item.baseRate || 0 });
      }
    });
    return map;
  }, [itemLibraryData]);

  const rateLookupMap = useMemo(() => {
    if (!itemLibraryData) return new Map();
    return createScaffoldRateLookupMap(itemLibraryData, state.groundworks);
  }, [itemLibraryData, state.groundworks]);

  const summaryData = useMemo(() => {
    if (!state.groundworks) return [];

    const dimensionValues: Record<string, string> = state.groundworks.scaffoldDimensionValues || {};

    const baseItems = generateBOQSummaryWithCategories(
      state.groundworks,
      'Scaffold',
      SCAFFOLD_CATEGORIES,
      undefined,
      null
    );

    const itemsWithRates = applyScaffoldRatesToSummary(baseItems, rateLookupMap);

    const consolidated: any[] = [];
    const processedCategories = new Set<string>();

    itemsWithRates.forEach((item) => {
      const config = CONSOLIDATE_FIELDS[item.category];
      if (!config) {
        consolidated.push(item);
        return;
      }

      // Skip individual rate fields — merged into the consolidated line
      if (config.rateFields.includes(item.fieldName)) return;

      if (item.fieldName === config.quantityField) {
        if (!processedCategories.has(item.category)) {
          processedCategories.add(item.category);

          const platformQuantity = item.quantity;

          // Build per-component breakdown rows — each uses its OWN field quantity
          const breakdownRows: BreakdownRow[] = config.rateFields.map((fieldName) => {
            const rateItem = itemsWithRates.find(
              (i) => i.fieldName === fieldName && i.category === item.category,
            );
            // Fall back to rateLookupMap directly when field has no data in the table
            // (getBOQColumnsForStep skips zero-quantity fields so rateItem can be undefined
            // even when the item library has a valid rate and resource code for the field)
            const fallbackRateInfo = rateLookupMap.get(fieldName);
            const rate = rateItem?.rate ?? fallbackRateInfo?.rate ?? 0;
            const resourceCode = (rateItem as any)?.resourceCode || fallbackRateInfo?.resourceCode || '';
            // Use the field's own quantity from the table, not the platform quantity
            const rowQuantity = rateItem?.quantity ?? platformQuantity;
            // Round to 2dp so the sum matches what's displayed per row
            const amount = Math.round(rowQuantity * rate * 100) / 100;
            const dbRate = computeScaffoldDbRate(fieldName, rate, dimensionValues);
            const dim = dimensionValues[fieldName];
            return {
              key: `breakdown-${item.category}-${fieldName}`,
              fieldName,
              description: getRateFieldLabel(fieldName, dimensionValues),
              quantity: rowQuantity,
              unit: "m²",
              rate,
              amount,
              resourceCode,
              dbRate: dbRate.toFixed(4),
              ...(dim && { multiplier: dim }),
              rowType: 'breakdown-row' as const,
            };
          });

          // Consolidated rate = sum of the Level & Compact / Crush 6F2 / Type1
          // rates. The Geotextile Layer rate is added into this sum ONLY when
          // that section's "Geotextile Layer" checkbox is on — it never
          // appears as its own line in the Rate Breakdown modal.
          const geotextileEnabled = dimensionValues[config.geotextileEnabledKey] === 'true';
          const combinedRate = breakdownRows.reduce((sum, r) => {
            if (r.fieldName === config.geotextileField) {
              return geotextileEnabled ? sum + r.rate : sum;
            }
            return sum + r.rate;
          }, 0);
          const totalAmount = combinedRate * platformQuantity;
          const description = buildConsolidatedDescription(
            config.crushField,
            config.type1Field,
            dimensionValues,
            geotextileEnabled,
          );

          consolidated.push({
            key: `consolidated-${item.category}`,
            fieldName: `consolidated-${item.category}`,
            apiFieldName: config.quantityField,
            description,
            quantity: platformQuantity,
            unit: "m²",
            rate: combinedRate,
            amount: totalAmount,
            category: item.category,
            breakdownRows,
            geotextileEnabled,
            geotextileField: config.geotextileField,
          });
        }
        return;
      }

      consolidated.push(item);
    });

    return consolidated.map((item, index) => ({
      key: item.key,
      itemNumber: `${index + 1}.0`,
      fieldName: item.fieldName,
      apiFieldName: item.apiFieldName ?? item.fieldName,
      description: item.description,
      quantity: item.quantity,
      unit: item.unit,
      rate: item.rate,
      amount: item.amount,
      category: item.category,
      resourceCode: (item as any).resourceCode || '',
      breakdownRows: item.breakdownRows,
      geotextileEnabled: item.geotextileEnabled,
      geotextileField: item.geotextileField,
    } as ScaffoldSummaryItem));
  }, [state.groundworks, rateLookupMap]);

  const markupFactor = (1 + settings.groundworks.overheadAndProfit / 100) * (1 + settings.groundworks.preliminaries / 100);

  // Build collapsible grouped structure
  const processedData = useMemo(() => {
    const rawProcessedData = summaryData.map((item) => {
      const roundedQty = Math.round(item.quantity);
      // For consolidated scaffold lines, match the Rate Breakdown modal's
      // Final Rate exactly: sum each component's marked-up rate AFTER
      // rounding it to 2dp, rather than rounding the pre-markup sum once.
      // Geotextile Layer only contributes when its checkbox is on.
      const displayRate = Array.isArray(item.breakdownRows) && item.breakdownRows.length > 0
        ? item.breakdownRows.reduce((sum, r) => {
            if (r.fieldName === item.geotextileField && !item.geotextileEnabled) return sum;
            return sum + parseFloat((r.rate * markupFactor).toFixed(2));
          }, 0)
        : parseFloat((item.rate * markupFactor).toFixed(2));
      return {
      key: item.key,
      category: item.itemNumber,
      description: item.description,
      quantity: String(roundedQty),
      unit: item.unit,
      rate: displayRate.toFixed(2),
      amount: Math.abs(roundedQty * displayRate).toFixed(2),
      categoryName: item.category,
      rowType: 'line-item' as const,
      hasData: true,
      isDiameterHeader: false,
      resourceCode: item.resourceCode || '',
      // Preserve the consolidated-line breakdown so it can be wired into the
      // tree as expandable children below when running in dev mode.
      breakdownRows: item.breakdownRows,
      geotextileEnabled: item.geotextileEnabled,
      geotextileField: item.geotextileField,
      apiSummaryItem: apiScaffoldSummaryByFieldName.get(item.apiFieldName ?? item.fieldName) || null,
      configViolation: configViolationsByField.get(item.apiFieldName ?? item.fieldName) ?? null,
      };
    });

    const categoryGroups = new Map<string, any[]>();
    rawProcessedData.forEach((item) => {
      if (!categoryGroups.has(item.categoryName)) {
        categoryGroups.set(item.categoryName, []);
      }
      categoryGroups.get(item.categoryName)!.push(item);
    });

    const result: any[] = [];
    let categoryIndex = 1;

    categoryGroups.forEach((items, categoryName) => {
      const sectionAmount = items.reduce((sum, item) => sum + parseFloat(item.amount), 0);

      const categoryChildren: any[] = [];
      items.forEach((item, idx) => {
        const lineItem: any = {
          ...item,
          category: `${categoryIndex}.${idx + 1}`,
        };

        // Dev-mode only: expose the consolidated breakdown rows as expandable
        // children of the consolidated line item. In production these stay
        // hidden — only the single consolidated row is visible.
        if (showDevMode && Array.isArray(item.breakdownRows) && item.breakdownRows.length > 0) {
          lineItem.children = item.breakdownRows;
        }

        categoryChildren.push(lineItem);
      });

      result.push({
        key: `category-${categoryName}`,
        category: `${categoryIndex}.0`,
        description: categoryName.toUpperCase(),
        quantity: '',
        unit: '',
        rate: '',
        amount: '',
        rowType: 'sub-header',
        isDiameterHeader: true,
        children: categoryChildren,
        sectionAmount,
        categoryName,
      });

      categoryIndex++;
    });

    // Graft ad-hoc extra rows on top of this otherwise-immutable computed
    // tree — nested additions merge onto the matching existing category row
    // (by `key`), and root additions become new top-level sections
    // continuing the same whole-number numbering the category rows above
    // use. Same convention GroundworksCutFillBillSummary.tsx /
    // FoundationsBillSummary.tsx follow. A consolidated line item's own
    // dev-mode `breakdownRows`/`children` are never an attachment point
    // (Actions only ever appears on section/category rows), so
    // `injectExtraRows`' generic recursion into `row.children` just walks
    // straight through them unchanged.
    const extraBuckets = scaffoldExtraItems ?? {};
    // Buckets whose attach-point row is actually present in this render's
    // computed tree — anything left over (see orphanedExtraRows below) had
    // its row disappear (e.g. the Scaffold take-off table was emptied,
    // dropping every category row it was nested under) and must not be
    // silently lost.
    const consumedAttachKeys = new Set<string>();
    const injectExtraRows = (rows: any[]): any[] =>
      rows.map((row) => {
        const bucket = extraBuckets[row.key];
        const existingChildren: any[] = row.children ?? [];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        consumedAttachKeys.add(row.key);
        const extraRows = buildNestedExtraRows(bucket, row.key, row.category, existingChildren.length, markupFactor);
        const mergedChildren = [...(ownChildren ?? []), ...extraRows];
        return {
          ...row,
          children: mergedChildren,
          ...(row.sectionAmount !== undefined && {
            sectionAmount: mergedChildren.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
          }),
        };
      });
    const injectedResult = injectExtraRows(result);
    let nextRootNumber = categoryIndex;
    const rootExtraRows = buildRootExtraRows(extraBuckets["__root__"] ?? [], "__root__", nextRootNumber, markupFactor);
    nextRootNumber += rootExtraRows.length;
    // Orphaned nested buckets (attach-point row no longer exists — e.g. its
    // parent category disappeared) wrap under a single synthetic "Additional
    // Items" header instead of landing as bare unlabelled rows — not a real
    // node itself (Edit/Delete never targets it, only its real children
    // match a node id), just keyed by the REAL attachKey so those children's
    // Edit/Delete/Qty edits still address the correct bucket in state.
    const orphanedExtraRows = Object.keys(extraBuckets)
      .filter((key) => key !== "__root__" && !consumedAttachKeys.has(key) && extraBuckets[key].length > 0)
      .map((key) => {
        const n = nextRootNumber++;
        const children = buildNestedExtraRows(extraBuckets[key], key, `${n}.0`, 0, markupFactor);
        // Category rows are keyed `category-${categoryName}` — recover the
        // original category name straight from the key so the orphaned
        // section keeps its real title instead of a generic placeholder.
        const originalLabel = key.startsWith("category-") ? key.slice("category-".length).toUpperCase() : "Additional Items";
        return {
          key: `orphaned-${key}`,
          category: `${n}.0`,
          description: originalLabel,
          rowType: "main-header",
          isExtra: true,
          attachKey: key,
          nodeType: "section",
          quantity: "",
          unit: "",
          rate: "",
          amount: "",
          dbRate: "",
          sectionAmount: children.reduce((sum, c) => sum + (parseFloat(c.amount || "0") || 0), 0),
          children,
        };
      });

    return [...injectedResult, ...rootExtraRows, ...orphanedExtraRows];
  }, [summaryData, state.groundworks, markupFactor, apiScaffoldSummaryByFieldName, showDevMode, scaffoldExtraItems, configViolationsByField]);

  // Depth of every row (real category rows AND grafted extra rows alike) in
  // the merged `processedData` tree — gates a section row's "Add Section"
  // Actions icon at MAX_SECTION_NESTING_DEPTH, counting pre-existing real
  // category nesting as well as already-added extra sections.
  const rowDepthMap = useMemo(() => buildSectionDepthMap(processedData), [processedData]);

  // Shared SummaryKey cell for both the consolidated line-item row (matched via
  // apiSummaryItem) and its breakdown-row children (matched via apiScaffoldChildByFieldName).
  // Renders blank when there's no persisted match (e.g. saved before the row's
  // current config) rather than falling back to a synthetic "BC (unsaved)" estimate.
  const renderSummaryKeyCell = (apiItem: BoqSummaryLineItem | null, record: any) => {
    if (!apiItem) return null;
    // Scaffold's consolidated row's own resourceCode is just the platform
    // field's (usually blank) — the real per-component codes live on
    // `children` (Level & Compact / Geotextile Layer / Crush 6F2 / Type1).
    // computeBcCalculation already sums "+"-joined resourceCode dbRates, so
    // fold the breakdown codes into one combined resourceCode instead of
    // carrying `children` through to the SummaryKey record.
    const breakdownItems = (apiItem.children ?? []) as Array<{ fieldName?: string; resourceCode?: string; multiplier?: number | string }>;
    const breakdownResourceCode = breakdownItems
      .map((r) => r.resourceCode)
      .filter(Boolean)
      .join('+');
    // Crush 6F2 and Type1 are the only breakdown fields with a rate-conversion
    // (m³→m² thickness) multiplier — Level & Compact / Geotextile Layer carry
    // none. Both convert independently (raw dim string → metres, same rule as
    // `computeScaffoldDbRate`/`getM3ToM2Multiplier`: >= 1 is mm ÷ 1000, < 1 is
    // already metres) and the consolidated row's own `multiplier` is their
    // PRODUCT — e.g. 100 (Crush 6F2) → 0.100m, 50 → 0.50m gives 0.100 × 0.50 = 0.05.
    const conversionFactors = breakdownItems
      .filter((r) => r.fieldName && SCAFFOLD_M3_CONVERSION_FIELDS.has(r.fieldName))
      .map((r) => parseThicknessMetres(r.multiplier))
      .filter((v): v is number => v !== null);
    const breakdownMultiplier = conversionFactors.length > 0
      ? conversionFactors.reduce((product, v) => product * v, 1)
      : undefined;
    // Drop `children` entirely — Scaffold's SummaryKey record never carries it.
    const { children: _apiItemChildren, ...apiItemWithoutChildren } = apiItem;
    const displayItem: BoqSummaryLineItem = {
      ...apiItemWithoutChildren,
      resourceCode: breakdownResourceCode || apiItem.resourceCode,
      multiplier: breakdownMultiplier ?? apiItem.multiplier,
    };
    const bcAmount = computeBcCalculation(displayItem, (code) => codeRateLookup.get(code)?.rate ?? 0).amount;
    const liveAmount = Number(record.amount ?? 0) || 0;
    const mismatch = Math.abs(liveAmount - bcAmount) > 0.01;
    const color = mismatch ? '#dc2626' : '#6b7280';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <Text style={{ fontSize: 12, color }}>
            £{(displayItem.amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
          <InfoCircleOutlined
            style={{ cursor: 'pointer', color: '#3b82f6' }}
            onClick={() => setSummaryKeyModalRecord(displayItem)}
          />
        </div>
        <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
          BC: £{bcAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </Text>
      </div>
    );
  };

  if (isLoadingItemLibrary) {
    return (
      <div style={{ padding: "16px", textAlign: "center" }}>
        <Spin size="large" />
        <div style={{ marginTop: "16px" }}>
          <Text className="text-gray-500">Loading item library rates...</Text>
        </div>
      </div>
    );
  }

  // An empty Scaffold take-off table alone must NOT hide already-saved
  // ad-hoc extra items — see GroundworksCutFillBillSummary.tsx's identical fix.
  const hasExtraItems = Object.values(scaffoldExtraItems ?? {}).some((bucket) => bucket.length > 0);

  return (
    summaryData.length === 0 && !hasExtraItems ? (
      <div style={{ padding: '16px' }}>
        <Text className="text-gray-500">
          No Scaffold data available. Please complete the Scaffold section first.
        </Text>
      </div>
    ) : (
      <div style={{ padding: '16px' }}>
        {/* Header */}
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: '16px', color: '#111827' }}>
              Scaffold Platforms Bill Summary
            </Title>
            <Tooltip title="Combined rate rows can be expanded to view individual rate components">
              <InfoCircleOutlined style={{ color: '#6b7280', fontSize: '14px', cursor: 'default' }} />
            </Tooltip>
          </div>
          <Space>
            {dynamicSummaryEnabled && !state.isViewMode && (
              <>
                <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection("__root__", null)}>
                  Add Section
                </Button>
                {/* Root-level "Add Item" button hidden per request (2026-09-01) — row-level Add Item icons remain.
                <Button size="small" icon={<PlusOutlined />} onClick={() => extraItemsApi.openAddItem("__root__", null)}>
                  Add Item
                </Button>
                */}
              </>
            )}
            <ExpandCollapseAllButton
              expanded={expandedRowKeys.length > 0}
              onToggle={() => {
                if (expandedRowKeys.length > 0) {
                  setExpandedRowKeys([]);
                } else {
                  const allKeys: React.Key[] = [];
                  const collect = (rows: any[]) => {
                    rows.forEach((row) => {
                      if (row.children?.length) {
                        allKeys.push(row.key);
                        collect(row.children);
                      }
                    });
                  };
                  collect(processedData);
                  setExpandedRowKeys(allKeys);
                }
              }}
            />
          </Space>
        </div>

        <style>
          {`
            .scaffold-summary-table .ant-table {
              border-radius: 0 !important;
            }
            .scaffold-summary-table .ant-table-container {
              border-radius: 0 !important;
            }
            .scaffold-summary-table .breakdown-row td {
              background-color: #f9fafb !important;
            }
          `}
        </style>
        <Table
          className="scaffold-summary-table"
          dataSource={processedData}
          expandable={{
            childrenColumnName: 'children',
            defaultExpandAllRows: false,
            expandedRowKeys: expandedRowKeys,
            onExpand: (expanded, record) => {
              if (expanded) {
                setExpandedRowKeys((prev) => [...prev, record.key]);
              } else {
                setExpandedRowKeys((prev) => prev.filter((k) => k !== record.key));
              }
            },
          }}
          columns={[
            {
              title: 'Item',
              dataIndex: 'category',
              key: 'category',
              width: 110,
              align: 'center',
              render: (value, record) => {
                if (isHeaderRowType(record.rowType) || record.rowType === 'description-header') {
                  return <Text strong>{value}</Text>;
                }
                if (record.rowType === 'breakdown-row') return '';
                return value;
              },
            },
            {
              title: 'Description',
              dataIndex: 'description',
              key: 'description',
              width: 350,
              render: (value, record) => {
                if (isHeaderRowType(record.rowType) || record.rowType === 'description-header') {
                  return <Text strong className="text-indigo-600">{value}</Text>;
                }
                if (record.rowType === 'description') {
                  return <Text italic className="text-gray-600">{value}</Text>;
                }
                if (record.rowType === 'breakdown-row') {
                  return (
                    <div style={{ paddingLeft: '8px' }}>
                      <Text style={{ color: '#6b7280', fontSize: '13px' }}>{value}</Text>
                      {showDevMode && (record.resourceCode || (record.resourceCodes && record.resourceCodes.length > 0)) && (
                        <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>
                          {record.resourceCode || record.resourceCodes!.map((c: { resourceCode: string; operator: '+' | '-' }, i: number) => (i === 0 ? c.resourceCode : `${c.operator} ${c.resourceCode}`)).join(' ')}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div>
                      <Text>{value}</Text>
                      {showDevMode && (record.resourceCode || (record.resourceCodes && record.resourceCodes.length > 0)) && (
                        <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2 }}>
                          {record.resourceCode || record.resourceCodes!.map((c: { resourceCode: string; operator: '+' | '-' }, i: number) => (i === 0 ? c.resourceCode : `${c.operator} ${c.resourceCode}`)).join(' ')}
                        </div>
                      )}
                    </div>
                    {showDevMode && Array.isArray(record.breakdownRows) && record.breakdownRows.length > 0 && (
                      <InfoCircleOutlined
                        style={{ color: '#1677ff', fontSize: '14px', cursor: 'pointer', flexShrink: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setBreakdownModalKey(record.key as string);
                        }}
                      />
                    )}
                    {(record as any).configViolation && (
                      <Tooltip title="Configure Headers required — click for details">
                        <WarningOutlined
                          style={{ color: '#faad14', fontSize: '14px', cursor: 'pointer', flexShrink: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfigViolationModalRecord((record as any).configViolation);
                          }}
                        />
                      </Tooltip>
                    )}
                  </div>
                );
              },
            },
            {
              title: 'Qty',
              dataIndex: 'quantity',
              key: 'quantity',
              width: 110,
              align: 'right',
              render: (value, record) => {
                if (
                  isHeaderRowType(record.rowType) ||
                  record.rowType === 'description' ||
                  record.rowType === 'description-header'
                ) return '';
                // Ad-hoc extra items are directly editable in-cell, same as
                // GroundworksCutFillBillSummary.tsx's own Qty column — existing
                // computed rows (including breakdown-row children) stay
                // read-only. Text amount type has no Qty at all, same
                // convention DynamicBillSummaryPanel's Qty column follows.
                if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
                  return (
                    <NumberInput
                      value={parseFloat(value) || 0}
                      onChange={(qty) => extraItemsApi.updateNode(record.attachKey, record.key, { qty })}
                      size="small"
                      decimalPlaces={2}
                      autoFocus={record.key === newlyAddedRowId}
                    />
                  );
                }
                if (!value || value === '' || value === '0' || value === '0.00' || value === '0.000') return '';
                const numValue = typeof value === 'number' ? value : parseFloat(value);
                if (isNaN(numValue)) return '';
                return (
                  <div style={{
                    textAlign: 'right',
                    color: record.rowType === 'breakdown-row' ? '#6b7280' : undefined,
                    fontSize: record.rowType === 'breakdown-row' ? '13px' : undefined,
                  }}>
                    {Math.round(numValue).toLocaleString('en-GB')}
                  </div>
                );
              },
            },
            {
              title: 'Unit',
              dataIndex: 'unit',
              key: 'unit',
              width: 110,
              align: 'center',
              render: (value: string, record) => {
                if (
                  isHeaderRowType(record.rowType) ||
                  record.rowType === 'description' ||
                  record.rowType === 'description-header'
                ) return '';
                // Same in-cell Select every extra-items Unit column uses.
                // Text amount type has no Unit at all.
                if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
                  return (
                    <Select
                      value={value || undefined}
                      onChange={(unit) => extraItemsApi.updateNode(record.attachKey, record.key, { unit })}
                      options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
                      placeholder="Unit"
                      size="small"
                      showSearch
                      allowClear
                      style={{ width: "100%" }}
                    />
                  );
                }
                const display = formatUnit(value);
                if (record.rowType === 'breakdown-row') {
                  return <span style={{ color: '#6b7280', fontSize: '13px' }}>{display}</span>;
                }
                return display;
              },
            },
            {
              title: 'Rate',
              dataIndex: 'rate',
              key: 'rate',
              width: 110,
              align: 'right',
              render: (value, record) => {
                if (
                  isHeaderRowType(record.rowType) ||
                  record.rowType === 'description' ||
                  record.rowType === 'description-header'
                ) return '';
                if (!value || value === '' || value === '0' || value === '0.00') return '';
                const numValue = typeof value === 'number' ? value : parseFloat(value);
                if (isNaN(numValue)) return '';
                return (
                  <div style={{
                    textAlign: 'right',
                    color: record.rowType === 'breakdown-row' ? '#6b7280' : undefined,
                    fontSize: record.rowType === 'breakdown-row' ? '13px' : undefined,
                  }}>
                    £{numValue.toFixed(2)}
                  </div>
                );
              },
            },
            ...(showDevMode ? [{
              title: 'DB Rate',
              dataIndex: 'dbRate',
              key: 'dbRate',
              width: 100,
              align: 'right' as const,
              render: (value: string, record: any) => {
                if (isHeaderRowType(record.rowType) || record.rowType === 'description-header' || record.rowType === 'description') return '';
                if (!value || value === '') return '';
                const numValue = parseFloat(value);
                if (isNaN(numValue) || numValue === 0) return '';
                return (
                  <div style={{ textAlign: 'right', color: '#9ca3af', fontSize: record.rowType === 'breakdown-row' ? '11px' : '12px' }}>
                    £{numValue.toFixed(4)}
                  </div>
                );
              },
            }] : []),
            {
              title: 'Amount',
              dataIndex: 'amount',
              key: 'amount',
              width: 120,
              align: 'right',
              render: (value, record) => {
                if (record.rowType === 'description') return '';

                if (
                  (isHeaderRowType(record.rowType) || record.rowType === 'description-header') &&
                  record.children?.length > 0
                ) {
                  const isExpanded = expandedRowKeys.includes(record.key);
                  if (!isExpanded && record.sectionAmount > 0) {
                    return (
                      <div style={{ textAlign: 'right' }}>
                        <span className="font-semibold">
                          £{record.sectionAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    );
                  }
                  return '';
                }

                if (record.rowType === 'breakdown-row') {
                  if (!value || value === '0.00') return '';
                  return (
                    <div style={{ textAlign: 'right', color: '#6b7280', fontSize: '13px' }}>
                      £{parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  );
                }

                // Text amount type (extra items only) — a free-typed value like
                // "Included"/"Rate Only" instead of a computed £ amount, same
                // convention DynamicBillSummaryPanel's own Amount column uses.
                if (record.amountType === 'text') {
                  return <div style={{ textAlign: 'right' }}><Text>{record.amountText}</Text></div>;
                }

                if (!value || value === '0.00') return '';
                return (
                  <div style={{ textAlign: 'right' }}>
                    <Text strong>
                      £{parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Text>
                  </div>
                );
              },
            },
            ...(showDevMode ? [{
              title: 'SummaryKey',
              dataIndex: 'apiSummaryItem',
              key: 'summaryKey',
              width: 150,
              align: 'right' as const,
              render: (value: BoqSummaryLineItem | null | undefined, record: any) => {
                if (isHeaderRowType(record.rowType) || record.rowType === 'description-header' || record.rowType === 'description') return '';
                if (record.isExtra) {
                  const node = findExtraNode(scaffoldExtraItems?.[record.attachKey] ?? [], record.key) as DynamicBillSummaryItemNode | null;
                  if (!node) return '';
                  const liveAmount = parseFloat(String(record.amount ?? '0')) || 0;
                  const lineItem: DynamicBillSummaryLineItem = {
                    key: node.id,
                    description: node.description,
                    quantity: node.qty,
                    unit: node.unit,
                    rate: node.rate,
                    amount: liveAmount,
                    amountType: node.amountType,
                    amountText: node.amountText,
                    resourceCode: node.resourceCode,
                    resourceCodes: node.resourceCodes,
                  };
                  const bc = computeDynamicBcCalculation(lineItem, (code) => codeRateLookup.get(code)?.rate ?? 0);
                  // Once saved, this extra item persists into the real BOQ document's
                  // `summary.scaffold` array under its own `key`/`id` (see
                  // flattenCutFillsExtraItemsToBoqLines, boqPayloadBuilder.ts) — same
                  // persisted-vs-live comparison every other row's SummaryKey shows.
                  // Before the first save, there's nothing to compare yet, so this
                  // falls back to a live-derived "unsaved" value instead — same
                  // convention DynamicBillSummaryPanel's own SummaryKey column uses.
                  const persisted = apiScaffoldSummaryByFieldName.get(record.key);
                  const persistedAmount = persisted?.amount ?? liveAmount;
                  const mismatch = !!persisted && Math.abs(liveAmount - persistedAmount) > 0.01;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : persisted ? '#6b7280' : '#f59e0b' }}>
                          £{persistedAmount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Text>
                        <InfoCircleOutlined
                          style={{ cursor: 'pointer', color: '#3b82f6' }}
                          onClick={() => setExtraSummaryKeyRecord({ live: lineItem, persisted: persisted ?? null })}
                        />
                      </div>
                      {bc && (
                        <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
                          BC: £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Text>
                      )}
                      {!persisted && <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>}
                    </div>
                  );
                }
                if (record.rowType === 'breakdown-row') {
                  return renderSummaryKeyCell(apiScaffoldChildByFieldName.get(record.fieldName) || null, record);
                }
                return renderSummaryKeyCell(value || null, record);
              },
            }] : []),
            ...(dynamicSummaryEnabled && !state.isViewMode ? [{
              title: 'Actions',
              key: 'extraActions',
              width: 150,
              align: 'center' as const,
              render: (_: unknown, record: any) => {
                if (record.rowType === 'description' || record.rowType === 'breakdown-row') return '';
                if (record.isExtra) {
                  const node = findExtraNode(scaffoldExtraItems?.[record.attachKey] ?? [], record.key);
                  if (!node) return '';
                  if (record.nodeType === 'section') {
                    return (
                      <Space size={4}>
                        <Tooltip title={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                          <Button
                            size="small"
                            icon={<FolderAddOutlined />}
                            title="Add Section"
                            disabled={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH}
                            onClick={() => extraItemsApi.openAddSection(record.attachKey, record.key)}
                          />
                        </Tooltip>
                        <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.attachKey, record.key)} />
                        <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(record.attachKey, node as DynamicBillSummarySectionNode)} />
                        <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey, record.key)} />
                      </Space>
                    );
                  }
                  return (
                    <Space size={4}>
                      <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(record.attachKey, node as DynamicBillSummaryItemNode)} />
                      <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey, record.key)} />
                    </Space>
                  );
                }
                // Existing computed row — immutable, but a category (section)
                // row can host grafted extra rows. A plain computed line-item
                // gets no Actions at all — only sections are attachment points.
                if (!isHeaderRowType(record.rowType)) {
                  return '';
                }
                {
                  const atMaxDepth = (rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH;
                  return (
                    <Space size={4}>
                      <Tooltip title={atMaxDepth ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                        <Button
                          size="small"
                          icon={<FolderAddOutlined />}
                          title="Add Section"
                          disabled={atMaxDepth}
                          onClick={() => extraItemsApi.openAddSection(record.key, null)}
                        />
                      </Tooltip>
                      <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key, null)} />
                    </Space>
                  );
                }
              },
            }] : []),
          ]}
          pagination={false}
          size="middle"
          bordered
          rowClassName={(record) => {
            const classes: string[] = [];
            // 'main-header' is only ever produced by newly-added extra root
            // sections (see buildRootExtraRows) — styled identically to the
            // fixed category ('sub-header') rows for visual consistency, same
            // as GroundworksCutFillBillSummary.tsx.
            if (isHeaderRowType(record.rowType)) classes.push('bg-indigo-50', 'font-semibold');
            else if (record.rowType === 'description-header') classes.push('bg-indigo-50', 'font-semibold');
            else if (record.rowType === 'description') classes.push('bg-gray-50');
            else if (record.rowType === 'breakdown-row') classes.push('breakdown-row');
            if (record.key === newlyAddedRowId) classes.push('scaffold-summary-row-added');
            return classes.join(' ');
          }}
          summary={(pageData) => {
            // Flatten all nested data recursively. A line-item row can now
            // carry extra grafted children (see extraItemsApi/injectExtraRows
            // above) as well as its own dev-mode breakdownRows — its own
            // amount still counts even when it also has children, so both
            // branches fire for that case instead of being mutually exclusive.
            const flattenAll = (items: readonly any[]): any[] => {
              let result: any[] = [];
              items.forEach((item) => {
                if (item.rowType === 'line-item') {
                  // Collect the line-item itself (its amount is the consolidated total)
                  result.push(item);
                }
                if (item.children?.length) {
                  result.push(...flattenAll(item.children));
                }
              });
              return result;
            };

            const flattenedData = flattenAll(pageData);
            const totalAmt = flattenedData.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

            return (
              <Table.Summary.Row style={{ backgroundColor: "#fafafa", fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <Text strong style={{ fontWeight: 700 }}>TOTAL SCAFFOLD PLATFORMS</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} />
                <Table.Summary.Cell index={3} />
                <Table.Summary.Cell index={4} />
                {showDevMode && <Table.Summary.Cell index={5} />}
                <Table.Summary.Cell index={showDevMode ? 6 : 5} align="right">
                  <span style={{ display: 'block', textAlign: 'right', fontWeight: 700 }}>
                    £{totalAmt.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </Table.Summary.Cell>
                {showDevMode && <Table.Summary.Cell index={7} />}
                {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={8} />}
              </Table.Summary.Row>
            );
          }}
        />

        <style>{`
          .scaffold-summary-table .ant-table-thead > tr > th {
            min-width: 110px !important;
            background-color: ${COLORS.tableHeaderBg} !important;
          }
          /* Newly-added extra Section/Item row (see newlyAddedRowId) — a brief
             highlight fade so the row is easy to spot the moment its parent
             auto-expands to reveal it. */
          @keyframes scaffold-summary-row-added-fade {
            0% { background-color: #fef3c7; }
            100% { background-color: transparent; }
          }
          .scaffold-summary-row-added > td {
            animation: scaffold-summary-row-added-fade 1.8s ease-out;
          }
          /* Smooth expand/collapse instead of antd's default instant toggle. */
          .scaffold-summary-table .ant-table-row-expand-icon {
            transition: transform 0.2s ease;
          }
          .scaffold-summary-table .ant-table-tbody > tr {
            transition: background-color 0.3s ease;
          }
        `}</style>

        {/* Rate breakdown modal for the consolidated "crushed concrete / Type 1" row */}
        {(() => {
          if (!breakdownModalKey) return null;
          let modalRecord: any = null;
          for (const section of processedData) {
            const found = (section.children ?? []).find((c: any) => c.key === breakdownModalKey);
            if (found) { modalRecord = found; break; }
          }
          if (!modalRecord || !Array.isArray(modalRecord.breakdownRows) || modalRecord.breakdownRows.length === 0) return null;

          const fmtGBP = (n: number) => n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          // Only Level & Compact / Crush 6F2 / Type1 are listed — Geotextile
          // Layer never appears as its own row here.
          const rows = (modalRecord.breakdownRows as BreakdownRow[])
            .filter((r) => r.fieldName !== modalRecord.geotextileField)
            .map((r) => ({
              key: r.key,
              description: r.description,
              unit: r.unit,
              rate: parseFloat((r.rate * markupFactor).toFixed(2)),
            }));
          const geotextileRow = (modalRecord.breakdownRows as BreakdownRow[])
            .find((r) => r.fieldName === modalRecord.geotextileField);
          const geotextileEnabled = !!modalRecord.geotextileEnabled;
          const geotextileMarkedUpRate = geotextileRow ? parseFloat((geotextileRow.rate * markupFactor).toFixed(2)) : 0;
          const finalRate = rows.reduce((s: number, r: { rate: number }) => s + r.rate, 0) + (geotextileEnabled ? geotextileMarkedUpRate : 0);

          return (
            <Modal
              title={`Rate Breakdown — ${modalRecord.description}`}
              open={true}
              onCancel={() => setBreakdownModalKey(null)}
              footer={[<Button key="close" onClick={() => setBreakdownModalKey(null)}>Close</Button>]}
              width={520}
            >
              <div style={{ fontSize: 13, color: '#374151' }}>
                <p style={{ margin: '0 0 12px' }}>
                  The consolidated rate is the sum of the rates below.
                </p>
                <Table
                  size="small"
                  bordered
                  pagination={false}
                  dataSource={rows}
                  columns={[
                    { title: 'Description', dataIndex: 'description', key: 'description' },
                    { title: 'Unit', dataIndex: 'unit', key: 'unit', align: 'center' as const, render: (v: string) => formatUnit(v) },
                    { title: 'Rate', dataIndex: 'rate', key: 'rate', align: 'right' as const, render: (v: number) => `£${v.toFixed(2)}` },
                  ]}
                  summary={() => (
                    <>
                      {geotextileEnabled && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0}>Geotextile Layer</Table.Summary.Cell>
                          <Table.Summary.Cell index={1} align="center">{formatUnit('m²')}</Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right">£{geotextileMarkedUpRate.toFixed(2)}</Table.Summary.Cell>
                        </Table.Summary.Row>
                      )}
                      <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 600 }}>
                        <Table.Summary.Cell index={0} colSpan={2} align="right">
                          <Text strong>Final Rate</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <Text strong>£{fmtGBP(finalRate)}</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    </>
                  )}
                />
              </div>
            </Modal>
          );
        })()}

        <Modal
          title="Configure Headers required"
          open={!!configViolationModalRecord}
          onCancel={() => setConfigViolationModalRecord(null)}
          footer={
            <Button type="primary" onClick={() => setConfigViolationModalRecord(null)}>
              OK
            </Button>
          }
        >
          {configViolationModalRecord && (
            <div>
              <Text strong>{configViolationModalRecord.section}</Text>
              <div style={{ marginTop: 8 }}>
                <Text>{configViolationModalRecord.message}</Text>
              </div>
            </div>
          )}
        </Modal>

        <Modal
          title="SummaryKey — Amount Calculation"
          open={!!summaryKeyModalRecord}
          onCancel={() => setSummaryKeyModalRecord(null)}
          footer={null}
        >
          {summaryKeyModalRecord && (
            <BcCalculationDetails
              record={summaryKeyModalRecord}
              getDbRate={(code) => codeRateLookup.get(code)?.rate ?? 0}
            />
          )}
        </Modal>

        <Modal
          title={extraSummaryKeyRecord?.persisted ? "SummaryKey — Amount Calculation" : "SummaryKey — Live Value (not yet in BOQ export)"}
          open={!!extraSummaryKeyRecord}
          onCancel={() => setExtraSummaryKeyRecord(null)}
          footer={null}
        >
          {extraSummaryKeyRecord && (() => {
            const { live, persisted } = extraSummaryKeyRecord;
            const bc = computeDynamicBcCalculation(live, (code) => codeRateLookup.get(code)?.rate ?? 0);
            return (
              <>
                <SummaryKeyRawFields record={(persisted ?? live) as unknown as Record<string, unknown>} />
                {bc && (
                  <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                    <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                    <Descriptions bordered size="small" column={1} style={{ marginTop: 8 }}>
                      <Descriptions.Item label="Computed Rate (rounded 2dp)">£{bc.rate.toFixed(2)}</Descriptions.Item>
                      <Descriptions.Item label="Quantity (rounded)">
                        {bc.quantity.toLocaleString('en-GB')} {formatUnit(live.unit)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Amount (rounded 2dp)">
                        £{bc.amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </Descriptions.Item>
                    </Descriptions>
                    <div style={{ marginTop: 8 }}>
                      {bc.steps.map((step, idx) => (
                        <div key={idx}>
                          <Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
        </Modal>

        {extraItemsApi.modals}
      </div>
    )
  );
};

export default ScaffoldBillSummary;
